import { FormEvent, useEffect, useMemo, useState } from "react";
import "./ProfileConfig.css";
import { userService } from "@/services/users/userService";
import { useUser } from "@/contexts";
import { blockService } from "@/services";
import type { BlockedUserRecord } from "@/services";

type ConfigItem = {
  id:
    | "notifications"
    | "blocked"
    | "verified"
    | "email"
    | "password"
    | "visibility"
    | "account";
  label: string;
  disabled?: boolean;
};

type PasswordFieldProps = {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  autoComplete?: string;
};

const PasswordField = ({
  label,
  value,
  onChange,
  placeholder,
  autoComplete,
}: PasswordFieldProps) => {
  const [show, setShow] = useState(false);

  return (
    <label>
      {label}
      <div className="password-input-wrapper">
        <input
          type={show ? "text" : "password"}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          autoComplete={autoComplete}
        />
        <button
          type="button"
          className="password-toggle-btn"
          onClick={() => setShow((prev) => !prev)}
          aria-label={show ? "Ocultar senha" : "Mostrar senha"}
        >
          {show ? (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
              <line x1="1" y1="1" x2="23" y2="23"></line>
            </svg>
          ) : (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
              <circle cx="12" cy="12" r="3"></circle>
            </svg>
          )}
        </button>
      </div>
    </label>
  );
};

const ProfileConfigScreen: React.FC = () => {
  const { user, userData, refreshUser } = useUser();

  const menuItems: ConfigItem[] = useMemo(
    () => [
      { id: "notifications", label: "Notificações", disabled: true },
      { id: "blocked", label: "Usuários bloqueados" },
      {
        id: "verified",
        label: "Mostrar que seu perfil é verificado",
        disabled: true,
      },
      { id: "email", label: "Mudar e-mail" },
      { id: "password", label: "Mudar senha" },
      { id: "visibility", label: "Visibilidade" },
      { id: "account", label: "Conta" },
    ],
    [],
  );

  const [activeItem, setActiveItem] = useState<ConfigItem["id"]>("blocked");

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");

  const [currentEmail, setCurrentEmail] = useState("");
  const [newEmail, setNewEmail] = useState("");

  const [emailStatus, setEmailStatus] = useState<string | null>(null);
  const [passwordStatus, setPasswordStatus] = useState<string | null>(null);
  const [closeStatus, setCloseStatus] = useState<string | null>(null);
  const [visibilityStatus, setVisibilityStatus] = useState<string | null>(null);
  const [visibilityOption, setVisibilityOption] = useState<
    "visible" | "invisible"
  >("visible");
  const [notificationSettings, setNotificationSettings] = useState({
    msgReceived: false,
    favoritesActions: false,
    profileAccess: false,
    offers: false,
    accountUpdates: false,
    billing: false,
    browserEnabled: false,
  });
  const [loadingEmail, setLoadingEmail] = useState(false);
  const [loadingPassword, setLoadingPassword] = useState(false);
  const [loadingClose, setLoadingClose] = useState(false);
  const [loadingVisibility, setLoadingVisibility] = useState(false);

  const [blockedUsers, setBlockedUsers] = useState<BlockedUserRecord[]>([]);
  const [blockedSearch, setBlockedSearch] = useState("");
  const [blockedSuggestions, setBlockedSuggestions] = useState<
    Array<{
      uid: string;
      username: string;
      profileImage: string | null;
      city?: string;
      state?: string;
    }>
  >([]);
  const [loadingBlocked, setLoadingBlocked] = useState(false);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [blockedStatus, setBlockedStatus] = useState<string | null>(null);

  // Carrega a visibilidade atual do usuário
  // visible: false => invisível; undefined/true => visível
  useEffect(() => {
    const current = userData?.visible === false ? "invisible" : "visible";
    setVisibilityOption(current);
  }, [userData?.visible]);

  const applyVisibility = async (next: "visible" | "invisible") => {
    setVisibilityOption(next);
    setVisibilityStatus(null);
    setLoadingVisibility(true);
    try {
      const result = await userService.changeVisibility(next === "visible");
      if (result.success) {
        setVisibilityStatus("Visibilidade atualizada com sucesso.");
        await refreshUser();
      } else if (result.error?.detail) {
        setVisibilityStatus(result.error.detail);
      } else {
        setVisibilityStatus("Não foi possível atualizar a visibilidade.");
      }
    } finally {
      setLoadingVisibility(false);
    }
  };

  const handleCloseAccount = async () => {
    const confirmClose = window.confirm("Deseja mesmo encerrar sua conta?");
    if (!confirmClose) return;
    setCloseStatus(null);
    setLoadingClose(true);
    try {
      const result = await userService.closeAccount();
      if (result.success) {
        setCloseStatus("Conta encerrada. Você será desconectado.");
      } else if (result.error?.detail) {
        setCloseStatus(result.error.detail);
      } else {
        setCloseStatus("Não foi possível encerrar a conta.");
      }
    } finally {
      setLoadingClose(false);
    }
  };

  const handleEmailSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setEmailStatus(null);
    if (!currentEmail || !newEmail || !currentPassword) {
      setEmailStatus("Preencha e-mail atual, novo e senha atual.");
      return;
    }
    setLoadingEmail(true);
    try {
      const result = await userService.changeEmail({
        currentEmail,
        currentPassword,
        newEmail,
      });
      if (result.success) {
        setEmailStatus("E-mail atualizado com sucesso.");
        setCurrentEmail("");
        setNewEmail("");
        setCurrentPassword("");
      } else if (result.error?.detail) {
        setEmailStatus(result.error.detail);
      } else {
        setEmailStatus("Não foi possível atualizar o e-mail.");
      }
    } finally {
      setLoadingEmail(false);
    }
  };

  const handlePasswordSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setPasswordStatus(null);
    if (!currentPassword || !newPassword || !confirmNewPassword) {
      setPasswordStatus("Preencha todos os campos de senha.");
      return;
    }
    if (newPassword !== confirmNewPassword) {
      setPasswordStatus("As senhas não conferem.");
      return;
    }
    setLoadingPassword(true);
    try {
      const result = await userService.changePassword({
        currentPassword,
        newPassword,
      });
      if (result.success) {
        setPasswordStatus("Senha atualizada com sucesso.");
        setCurrentPassword("");
        setNewPassword("");
        setConfirmNewPassword("");
      } else if (result.error?.detail) {
        setPasswordStatus(result.error.detail);
      } else {
        setPasswordStatus("Não foi possível atualizar a senha.");
      }
    } finally {
      setLoadingPassword(false);
    }
  };

  const refreshBlockedUsers = async () => {
    if (!user?.uid) return;
    setLoadingBlocked(true);
    try {
      const list = await blockService.listBlockedUsers(user.uid);
      setBlockedUsers(list);
    } catch (err) {
      console.error("Erro ao carregar bloqueados", err);
      setBlockedUsers([]);
    } finally {
      setLoadingBlocked(false);
    }
  };

  useEffect(() => {
    if (activeItem !== "blocked") return;
    refreshBlockedUsers();
  }, [activeItem, user?.uid]);

  useEffect(() => {
    if (activeItem !== "blocked") return;
    if (!user?.uid) return;

    const gender = String(userData?.gender || "").trim();
    const keyword = blockedSearch;

    if (!gender || !keyword || keyword.trim().length < 2) {
      setBlockedSuggestions([]);
      return;
    }

    const handle = window.setTimeout(async () => {
      setLoadingSuggestions(true);
      try {
        const list = await blockService.listAutocompleteUsers({
          blockerId: user.uid,
          gender,
          keyword,
          pageSize: 10,
        });
        setBlockedSuggestions(list);
      } catch (err) {
        console.error("Erro ao buscar usuários", err);
        setBlockedSuggestions([]);
      } finally {
        setLoadingSuggestions(false);
      }
    }, 250);

    return () => window.clearTimeout(handle);
  }, [activeItem, blockedSearch, user?.uid, userData?.seeking]);

  const handleBlockUser = async (target: {
    uid: string;
    username: string;
    profileImage: string | null;
  }) => {
    if (!user?.uid) return;
    setBlockedStatus(null);
    try {
      await blockService.blockUser(user.uid, target.uid, {
        username: target.username,
        profileImage: target.profileImage,
      });
      setBlockedStatus("Usuário bloqueado com sucesso.");
      setBlockedSearch("");
      setBlockedSuggestions([]);
      await refreshBlockedUsers();
    } catch (err) {
      console.error("Erro ao bloquear usuário", err);
      setBlockedStatus("Não foi possível bloquear o usuário.");
    }
  };

  const handleUnblockUser = async (blockedId: string) => {
    if (!user?.uid) return;
    setBlockedStatus(null);
    try {
      await blockService.unblockUser(user.uid, blockedId);
      setBlockedStatus("Usuário desbloqueado.");
      await refreshBlockedUsers();
    } catch (err) {
      console.error("Erro ao desbloquear usuário", err);
      setBlockedStatus("Não foi possível desbloquear.");
    }
  };

  const renderContent = () => {
    switch (activeItem) {
      case "blocked":
        return (
          <div className="config-card">
            <div className="config-card-header">
              <h2>Usuários bloqueados</h2>
              <p>Revise e desbloqueie perfis quando quiser.</p>
            </div>

            <div className="config-form">
              <label>
                Buscar usuário por nickname
                <input
                  type="text"
                  placeholder="Digite ao menos 2 letras"
                  value={blockedSearch}
                  onChange={(e) => {
                    setBlockedStatus(null);
                    setBlockedSearch(e.target.value);
                  }}
                />
              </label>

              {loadingSuggestions ? (
                <div className="config-empty">Buscando...</div>
              ) : blockedSuggestions.length > 0 ? (
                <div className="blocked-autocomplete">
                  {blockedSuggestions.map((u) => (
                    <div key={u.uid} className="blocked-row">
                      <div className="blocked-left">
                        {u.profileImage ? (
                          <img
                            className="blocked-avatar"
                            src={u.profileImage}
                            alt={u.username}
                          />
                        ) : (
                          <div className="blocked-avatar placeholder" />
                        )}
                        <div className="blocked-meta">
                          <div className="blocked-username">{u.username}</div>
                          <div className="blocked-sub">
                            {u.city || ""}
                            {u.city && u.state ? ", " : ""}
                            {u.state || ""}
                          </div>
                        </div>
                      </div>
                      <button
                        type="button"
                        className="btn-primary"
                        onClick={() => handleBlockUser(u)}
                      >
                        Bloquear
                      </button>
                    </div>
                  ))}
                </div>
              ) : blockedSearch.trim().length >= 2 ? (
                <div className="config-empty">Nenhum usuário encontrado.</div>
              ) : null}

              {blockedStatus && (
                <div className="config-status success">{blockedStatus}</div>
              )}
            </div>

            <div className="divider" />

            <div className="list-block">
              {loadingBlocked ? (
                <div className="config-empty">Carregando...</div>
              ) : blockedUsers.length === 0 ? (
                <div className="config-empty">
                  Nenhum usuário bloqueado no momento.
                </div>
              ) : (
                <div className="blocked-list">
                  {blockedUsers.map((b) => (
                    <div key={b.id} className="blocked-row">
                      <div className="blocked-left">
                        {b.blockedUserData?.profileImage ? (
                          <img
                            className="blocked-avatar"
                            src={b.blockedUserData.profileImage}
                            alt={b.blockedUserData.username}
                          />
                        ) : (
                          <div className="blocked-avatar placeholder" />
                        )}
                        <div className="blocked-meta">
                          <div className="blocked-username">
                            {b.blockedUserData?.username || "Usuário"}
                          </div>
                        </div>
                      </div>
                      <button
                        type="button"
                        className="btn-ghost"
                        onClick={() => handleUnblockUser(b.blockedId)}
                      >
                        Desbloquear
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        );
      case "account":
        return (
          <div className="config-card">
            <div className="config-card-header">
              <h2>Conta</h2>
              <p>
                Encerrar sua conta impede novos acessos até reativação manual.
              </p>
            </div>
            <div className="config-options">
              <div className="config-option-row">
                <div>
                  <div>Encerrar conta</div>
                  <small className="config-muted">
                    Você deseja realmente encerrar sua conta? Essa ação não é
                    irreversível. Se você deseja apenas ficar invisível, use a
                    opção de "Visibilidade".
                  </small>
                </div>
                <button
                  type="button"
                  className="btn-danger"
                  disabled={loadingClose}
                  onClick={handleCloseAccount}
                >
                  {loadingClose ? "Encerrando..." : "Encerrar conta"}
                </button>
              </div>
              {closeStatus && (
                <div className="config-status success">{closeStatus}</div>
              )}
            </div>
          </div>
        );
      case "notifications":
        return (
          <div className="config-card">
            <div className="config-card-header">
              <h2>Notificações</h2>
              <p>Escolha o que deseja receber por e-mail e dispositivo.</p>
            </div>

            <div className="config-section">
              <div className="section-title">Notificações por e-mail</div>
              <div className="section-subtitle">
                Escolha quais atualizações você deseja receber por e-mail.
              </div>

              <div className="toggle-list">
                <label className="toggle-row">
                  <span>Alguém me enviou uma mensagem</span>
                  <input
                    type="checkbox"
                    checked={notificationSettings.msgReceived}
                    onChange={(e) =>
                      setNotificationSettings((prev) => ({
                        ...prev,
                        msgReceived: e.target.checked,
                      }))
                    }
                  />
                </label>
                <label className="toggle-row">
                  <span>Ações de perfis favoritos</span>
                  <input
                    type="checkbox"
                    checked={notificationSettings.favoritesActions}
                    onChange={(e) =>
                      setNotificationSettings((prev) => ({
                        ...prev,
                        favoritesActions: e.target.checked,
                      }))
                    }
                  />
                </label>
                <label className="toggle-row">
                  <span>Alguém acessou o meu perfil</span>
                  <input
                    type="checkbox"
                    checked={notificationSettings.profileAccess}
                    onChange={(e) =>
                      setNotificationSettings((prev) => ({
                        ...prev,
                        profileAccess: e.target.checked,
                      }))
                    }
                  />
                </label>
                <label className="toggle-row">
                  <span>Ofertas exclusivas e novidades</span>
                  <input
                    type="checkbox"
                    checked={notificationSettings.offers}
                    onChange={(e) =>
                      setNotificationSettings((prev) => ({
                        ...prev,
                        offers: e.target.checked,
                      }))
                    }
                  />
                </label>
                <label className="toggle-row">
                  <span>Atualizações sobre sua conta</span>
                  <input
                    type="checkbox"
                    checked={notificationSettings.accountUpdates}
                    onChange={(e) =>
                      setNotificationSettings((prev) => ({
                        ...prev,
                        accountUpdates: e.target.checked,
                      }))
                    }
                  />
                </label>
                <label className="toggle-row">
                  <span>Informações sobre cobranças</span>
                  <input
                    type="checkbox"
                    checked={notificationSettings.billing}
                    onChange={(e) =>
                      setNotificationSettings((prev) => ({
                        ...prev,
                        billing: e.target.checked,
                      }))
                    }
                  />
                </label>
              </div>
            </div>

            <div className="config-section">
              <div className="section-title">Notificações do dispositivo</div>
              <div className="section-subtitle">
                Receba alertas instantâneos sobre novas mensagens.
              </div>

              <label className="toggle-row">
                <span>Notificações do navegador estão ativas</span>
                <input
                  type="checkbox"
                  checked={notificationSettings.browserEnabled}
                  onChange={(e) =>
                    setNotificationSettings((prev) => ({
                      ...prev,
                      browserEnabled: e.target.checked,
                    }))
                  }
                />
              </label>
            </div>
          </div>
        );
      case "email":
        return (
          <div className="config-card">
            <div className="config-card-header">
              <h2>Mudar e-mail</h2>
              <p>Atualize seu e-mail de acesso.</p>
            </div>
            <form className="config-form" onSubmit={handleEmailSubmit}>
              <label>
                E-mail atual
                <input
                  type="email"
                  placeholder="Digite seu e-mail"
                  value={currentEmail}
                  onChange={(e) => setCurrentEmail(e.target.value)}
                />
              </label>
              <PasswordField
                label="Senha atual (para confirmar)"
                placeholder="Digite a senha atual"
                value={currentPassword}
                onChange={setCurrentPassword}
                autoComplete="current-password"
              />
              <label>
                Novo e-mail
                <input
                  type="email"
                  placeholder="Digite o novo e-mail"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                />
              </label>
              {emailStatus && (
                <div className="config-status success">{emailStatus}</div>
              )}
              <button
                type="submit"
                className="btn-primary"
                disabled={
                  loadingEmail || !currentEmail || !newEmail || !currentPassword
                }
              >
                {loadingEmail ? "Salvando..." : "Salvar novo e-mail"}
              </button>
            </form>
          </div>
        );
      case "password":
        return (
          <div className="config-card">
            <div className="config-card-header">
              <h2>Mudar senha</h2>
              <p>Altere sua senha para manter sua conta segura.</p>
            </div>
            <form className="config-form" onSubmit={handlePasswordSubmit}>
              <PasswordField
                label="Senha atual"
                placeholder="Digite a senha atual"
                value={currentPassword}
                onChange={setCurrentPassword}
                autoComplete="current-password"
              />
              <PasswordField
                label="Nova senha"
                placeholder="Digite a nova senha"
                value={newPassword}
                onChange={setNewPassword}
                autoComplete="new-password"
              />
              <PasswordField
                label="Confirmar a nova senha"
                placeholder="Confirme a nova senha"
                value={confirmNewPassword}
                onChange={setConfirmNewPassword}
                autoComplete="new-password"
              />
              {passwordStatus && (
                <div className="config-status">{passwordStatus}</div>
              )}
              <button
                type="submit"
                className="btn-primary"
                disabled={
                  loadingPassword ||
                  !currentPassword ||
                  !newPassword ||
                  !confirmNewPassword
                }
              >
                {loadingPassword ? "Salvando..." : "Salvar a nova senha"}
              </button>
            </form>
          </div>
        );
      case "visibility":
        return (
          <div className="config-card">
            <div className="config-card-header">
              <h2>Visibilidade</h2>
              <p>Escolha como seu perfil aparece para outras pessoas.</p>
            </div>
            <div className="visibility-options">
              <label className="radio-row">
                <input
                  type="radio"
                  name="visibility"
                  value="visible"
                  checked={visibilityOption === "visible"}
                  onChange={() => applyVisibility("visible")}
                  disabled={loadingVisibility}
                />
                <div>
                  <div className="radio-title">Visível</div>
                  <div className="radio-subtitle">
                    Seu perfil aparecerá nas listas e buscas.
                  </div>
                </div>
              </label>

              <label className="radio-row">
                <input
                  type="radio"
                  name="visibility"
                  value="invisible"
                  checked={visibilityOption === "invisible"}
                  onChange={() => applyVisibility("invisible")}
                  disabled={loadingVisibility}
                />
                <div>
                  <div className="radio-title">Invisível</div>
                  <div className="radio-subtitle">
                    Seu perfil não aparecerá nas listas e buscas, mas poderá ser
                    visto por pessoas com quem trocou mensagens.
                  </div>
                </div>
              </label>
            </div>

            {visibilityStatus && (
              <div className="config-status success">{visibilityStatus}</div>
            )}
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="profile-container container">
      <div className="profile-config-grid">
        <aside className="config-sidebar">
          <div className="config-menu">
            {menuItems.map((item) => (
              <button
                key={item.id}
                className={`config-menu-item ${
                  activeItem === item.id ? "active" : ""
                } ${item.disabled ? "disabled" : ""}`}
                onClick={() => {
                  if (item.disabled) return;
                  setActiveItem(item.id);
                }}
                type="button"
                disabled={item.disabled}
              >
                <span className={item.disabled ? "menu-label-disabled" : ""}>
                  {item.label}
                </span>
                <span className="menu-chevron">›</span>
              </button>
            ))}
          </div>
        </aside>

        <section className="config-content-panel">{renderContent()}</section>
      </div>
    </div>
  );
};

export default ProfileConfigScreen;
